import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    ViewChild
} from "@angular/core";
import { UserStore } from "@app/services/stores/UserStore";
import { isPOBoxAddress } from "@app/util/is-po-box-address.function";
import { take } from "rxjs/operators";
import { PropertiesService } from "@app/services/properties.service";
import { Property } from "@app/models/Property";
import { CountryAndStateApi } from "@app/services/apiServices/CountryAndStateApi";
import { NetworkErrorService } from "@app/services/NetworkError.service";
import { State } from "@app/models/State";
import { Country } from "@app/models/Country";
import { NgModel } from "@angular/forms";
import { GuestPaymentStore } from "@app/services/stores/GuestPaymentStore";

export type AddressFormComponentProps = {
    disablePOBoxes?: boolean,
}

export type AddressFormData = {
    address1: string,
    address2: string,
    unit: string,
    city: string,
    state: string,
    zip: string,
    country: string,
}

@Component({
    selector: 'app-card-address',
    template: require('./card-address.component.html')
})
export class CardAddressComponent implements OnInit {

    @Input() props: AddressFormComponentProps;
    @Input() selectProperty = true;

    @Output() addressFormValidate = new EventEmitter<boolean>()

    @ViewChild('cardAddressForm', { static: true }) private cardAddressForm;

    private properties: Property[] = [];

    UNSELECTED = -1;
    selectedPropertyIndex: number = this.UNSELECTED;
    cardPaymentCountries: Country[] = [];
    allStates: State[] = [];
    countryStates: State[] = [];
    countryRequiresState: boolean = true;
    countryRequiresPostalCode: boolean = true;
    countryIsUSA: boolean = true;
    countryIsCAN: boolean = false;
    stateLabel: string;
    zipCodeLabel: string;
    stateErrorMsg: string;
    zipErrorMessage: string;
    useCurrentData: boolean = false;
    prefillAddressForm = false;

    formValues = {
        address1: "",
        address2: "",
        unit: "",
        city: "",
        state: "",
        zipCode: "",
        country: "",
    }

    formErrors = {
        address1: false,
        noPoBox1: false,
        noPoBox2: false,
        city: false,
        state: false,
        zipCode: false
    }

    constructor(@Inject(UserStore) private userStore: UserStore,
                @Inject(PropertiesService) private propertiesService: PropertiesService,
                @Inject(CountryAndStateApi) private countryAndStateApi: CountryAndStateApi,
                @Inject(GuestPaymentStore) private  guestPaymentStore: GuestPaymentStore,
                @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService) {
    }

    private validateFormAndEmit() {
        if (this.allRequiredFields() &&
            this.noFormErrors()) {
            this.addressFormValidate.emit(true);
        } else {
            this.addressFormValidate.emit(false);
        }
    }

    getFormData(): AddressFormData {
        return {
            address1: this.formValues.address1,
            address2: this.formValues.address2,
            unit: this.formValues.unit,
            city: this.formValues.city,
            state: this.formValues.state,
            zip: this.formValues.zipCode,
            country: this.formValues.country,
        }
    }

    setFormData(data: AddressFormData) {
        this.formValues = {
            address1: data.address1,
            address2: data.address2,
            unit: data.unit,
            city: data.city,
            state: data.state,
            zipCode: data.zip,
            country: data.country,
        }
        this.selectedPropertyIndex = this.properties.findIndex(property => {
            if (property.address.line1 === data.address1 &&
                property.address.line2 === data.address2 &&
                property.address.city === data.city &&
                property.address.state === data.state) {
                return true;
            }

            return false;
        })

        this.useCurrentData = true;
    }

    allRequiredFields(): boolean {

        let allFieldsFound: boolean = this.formValues.address1 !== "" &&
            this.formValues.city !== "";

        if (allFieldsFound && (this.countryIsUSA || this.countryIsCAN)) {
            allFieldsFound = this.formValues.state !== "" &&
                this.formValues.zipCode !== "";
        }

        return allFieldsFound;
    }

    noFormErrors(): boolean {
        return !this.formErrors.address1 &&
            !this.formErrors.zipCode &&
            !this.formErrors.state &&
            !this.formErrors.noPoBox1 &&
            !this.formErrors.noPoBox2 &&
            !this.formErrors.city
    }


    ngOnInit(): void {
        if (this.selectProperty) {
            this.propertiesService.getProperties().pipe(take(1)).subscribe({
                next: properties => {
                    this.properties = properties;
                },
                error: (error) => {
                    console.error('Found error retrieving properties for card address')
                    console.error(error);
                }
            });
        } else {
            this.properties = [this.guestPaymentStore.getProperty()];
            if (this.guestPaymentStore.getCardIsPrefillAddress()) {
                this.prefillAddressForm = true;
                this.toggleSameAsProperty();
            } else if (this.guestPaymentStore.getCardUpdate()) {
                let billingAddress = this.guestPaymentStore.getCardUpdate().billingAddress;
                this.formValues = {
                    address1: billingAddress.line1 || "",
                    address2: billingAddress.line2 || "",
                    unit: billingAddress.unit || "",
                    city: billingAddress.city || "",
                    state: billingAddress.state || "",
                    zipCode: billingAddress.zipcode || "",
                    country: billingAddress.country || "USA",
                }
                this.useCurrentData = true;
            }
        }
        this.refreshCountries();
        this.refreshStates()
    }
    clearFormData() {
        this.formValues = {
            address1: "",
            address2: "",
            unit: "",
            city: "",
            state: "",
            zipCode: "",
            country: "USA",
        }
    }

    propertyDropdownChange() {
        this.clearFormData()

        if (this.selectedPropertyIndex > this.UNSELECTED) {
            const property = this.properties[this.selectedPropertyIndex];
            this.formValues = {
                address1: property.address.line1 || "",
                address2: property.address.line2 || "",
                unit: property.address.unit || "",
                city: property.address.city || "",
                state: property.address.state || "",
                zipCode: property.address.postalCode || "",
                country: property.address.country || "USA",
            }
        }

        this.validateFormAndEmit();

    }

    private refreshStates() {
        const statesNetworkError = () => {
            this.networkErrorService.handleNetworkError("Failed to fetch states", false)
                .pipe(take(1)).subscribe({
                    next: shouldRetry => {
                        if (shouldRetry) this.refreshStates()
                    },
                    error: () => {}
                })
        }
        this.countryAndStateApi.getAllStates().pipe(take(1)).subscribe({
            next: resp => {
                if (resp && resp.data) {
                    this.allStates = resp.data;
                    this.updateDisplayForCountryCode()
                } else statesNetworkError()
            },
            error: () => statesNetworkError
        })
    }

    onBlur(modelControl: NgModel) {
        if(!modelControl.touched || modelControl.pristine) {
            return;
        }
        switch(modelControl.name) {
            case "address1":
                if(this.props.disablePOBoxes && isPOBoxAddress(modelControl.value)) {
                    this.formErrors.noPoBox1 = true;
                } else {
                    this.formErrors.address1 = modelControl.status === "INVALID";
                }
                break;
            case "address2":
                if(this.props.disablePOBoxes && isPOBoxAddress(modelControl.value)) {
                    this.formErrors.noPoBox2 = true;
                }
                break;
            case "city":
                this.formErrors.city = modelControl.status === "INVALID";
                break;
            case "state":
                this.formErrors.state = modelControl.status === "INVALID";
                break;
        }

        this.validateFormAndEmit();
    }

    zipCodeChange(input) {
        this.formErrors.zipCode = false;
        const inputValue = input.target.value;
        if (inputValue === "" && this.countryRequiresPostalCode) {
            // we can only check to see if there is a value as we don't know all the other validation for other countries
            this.formErrors.zipCode = true;

        } else if (this.countryIsUSA) {
            //do usa logic
            this.formErrors.zipCode =
                !((inputValue && inputValue.length === 5) || (inputValue && inputValue.length === 10 && inputValue.charAt(5) === '-'));

        } else if (this.countryIsCAN) {
            const CAN_POSTAL_CODE_TEST = /[a-zA-Z]\d[a-zA-Z] ?\d[a-zA-Z]\d/g
            this.formErrors.zipCode = !CAN_POSTAL_CODE_TEST.test(inputValue);
        }

        this.validateFormAndEmit()

    }

    onCountryChange(countryValue) {
        this.updateDisplayForCountryCode(countryValue.value);
        this.validateFormAndEmit();
    }

    updateDisplayForCountryCode(code?: string) {

        if (!this.useCurrentData) {
            if(!code || code === "") {
                code = "USA";
                this.formValues.country = "USA"
            }
        } else {
            code = this.formValues.country;
        }

        this.countryIsUSA = code === "USA"
        this.countryIsCAN = code === "CAN"
        this.countryStates = this.allStates.filter(state => state.country === code);
        const currentCountry = (this.cardPaymentCountries || []).filter(country => country.code === code)[0]
        this.countryRequiresPostalCode = currentCountry ? currentCountry.postalCodeRequired : true
        this.countryRequiresState = this.countryIsUSA || this.countryIsCAN
        this.stateLabel = this.countryIsUSA ? "State" : "Province";
        this.stateErrorMsg = this.countryIsUSA ? "Please enter a State." : "Please enter a Province.";
        this.zipCodeLabel = this.countryIsUSA ? "Zip Code" : "Postal Code";
        this.zipErrorMessage = this.countryIsUSA ? "Please enter a valid zip code." : "Please enter a valid postal code."

        if (!this.useCurrentData) {
            this.formValues.state = "";
            this.formValues.zipCode = "";
        }

        this.formErrors = {
            address1: false,
            noPoBox1: false,
            noPoBox2: false,
            city: false,
            state: false,
            zipCode: false
        }

        this.useCurrentData = false;

    }

    private refreshCountries() {
        const countriesNetworkError = () => {
            this.networkErrorService.handleNetworkError("Failed to fetch countries", false)
                .pipe(take(1)).subscribe({
                    next: shouldRetry => {
                        if (shouldRetry) this.refreshCountries()
                    },
                    error: () => {}
                })
        }
        this.countryAndStateApi.getAllCountries()
            .pipe(take(1)).subscribe({
                next: resp => {
                    if (resp && resp.data) {
                        this.cardPaymentCountries = resp.data.filter(country => country.ofacSanctioned === false)
                    } else countriesNetworkError()
                },
                error: () => countriesNetworkError
            });
    }

    toggleSameAsProperty() {
        if (this.properties?.length > 0 && this.prefillAddressForm) {
            const property = this.properties[0];
            this.selectedPropertyIndex = 0; //disables modifying property fields
            this.formValues = {
                address1: property.address.line1 || "",
                address2: property.address.line2 || "",
                unit: property.address.unit || "",
                city: property.address.city || "",
                state: property.address.state || "",
                zipCode: property.address.postalCode || "",
                country: property.address.country || "USA",
            }
            this.useCurrentData = true;
        } else {
            this.selectedPropertyIndex = this.UNSELECTED; //disable property changes
            this.clearFormData();
        }
        this.guestPaymentStore.setCardIsPrefillAddress(this.prefillAddressForm);
        this.validateFormAndEmit();
    }
}

